import React, { useState, useEffect, useCallback } from 'react';
import {
  Row, Col, Card, Select, Typography, Tag, Space, Button, Badge, Empty, Spin,
  Statistic, Divider,
} from 'antd';
import {
  BulbOutlined, ReloadOutlined, RobotOutlined, RightOutlined,
} from '@ant-design/icons';
import { getEvents, getSmartInsights, getFunnel } from '../api';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const PRIMARY = '#0F1D6B';
const TEAL    = '#00838F';

const SENTIMENT = {
  positive: { bg: '#dcfce7', border: '#16a34a', text: '#166534' },
  negative: { bg: '#fee2e2', border: '#ef4444', text: '#991b1b' },
  neutral:  { bg: '#e0f2fe', border: '#0284c7', text: '#075985' },
};

export default function SmartInsights() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(null);
  const [insights, setInsights] = useState([]);
  const [funnel, setFunnel] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getEvents().then(r => {
      const ev = r.data.data || [];
      setEvents(ev);
      const a = ev.find(e => e.status === 'active') || ev[0];
      if (a) setEventId(a.id);
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!eventId) return;
    setLoading(true);
    Promise.all([
      getSmartInsights({ event_id: eventId }),
      getFunnel({ event_id: eventId }),
    ])
      .then(([ins, f]) => {
        setInsights(ins.data.data?.insights || []);
        setFunnel(f.data.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  const eventName = events.find(e => e.id === eventId)?.event_name || '';
  const maxFunnel = Math.max(...funnel.map(f => f.count), 1);

  return (
    <div>
      <Row align="middle" justify="space-between" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={4} style={{ margin: 0, color: PRIMARY }}>
            <BulbOutlined style={{ marginRight: 8, color: '#f59e0b' }} />Smart Insights
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Automated narrative summary — your event story told with data
          </Text>
        </Col>
        <Col>
          <Space>
            <Select value={eventId} onChange={setEventId} style={{ width: 240 }} placeholder="Event">
              {events.map(e => (
                <Option key={e.id} value={e.id}>
                  <Badge color={e.status === 'active' ? 'green' : 'default'} text={e.event_name} />
                </Option>
              ))}
            </Select>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
          </Space>
        </Col>
      </Row>

      {loading ? <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div> : (
        <>
          {/* Hero narrative card */}
          <Card style={{
            borderRadius: 12, marginBottom: 16,
            background: 'linear-gradient(135deg, #1e293b 0%, #0F1D6B 50%, #00838F 100%)',
          }}>
            <Row gutter={16} align="middle">
              <Col flex="auto">
                <Space direction="vertical" size={4}>
                  <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, letterSpacing: 4, textTransform: 'uppercase' }}>
                    🤖 AI Event Report
                  </Text>
                  <Title level={3} style={{ color: '#fff', margin: 0 }}>
                    {eventName}
                  </Title>
                  <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14 }}>
                    Generated {new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                  </Text>
                </Space>
              </Col>
              <Col>
                <RobotOutlined style={{ fontSize: 60, color: 'rgba(255,255,255,0.3)' }} />
              </Col>
            </Row>
          </Card>

          {/* Insights grid */}
          {insights.length === 0 ? <Empty description="Not enough data to generate insights yet" /> : (
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              {insights.map((ins, i) => {
                const s = SENTIMENT[ins.sentiment] || SENTIMENT.neutral;
                return (
                  <Col xs={24} md={12} key={i}>
                    <Card style={{
                      borderRadius: 10, height: '100%',
                      background: s.bg, borderLeft: `5px solid ${s.border}`,
                    }}>
                      <Space align="start">
                        <div style={{ fontSize: 32, lineHeight: 1 }}>{ins.icon}</div>
                        <div>
                          <Text strong style={{ color: s.text, fontSize: 13, textTransform: 'uppercase', letterSpacing: 1 }}>
                            {ins.title}
                          </Text>
                          <Paragraph style={{ marginTop: 6, marginBottom: 0, color: '#1e293b', fontSize: 14, lineHeight: 1.6 }}>
                            {ins.text}
                          </Paragraph>
                        </div>
                      </Space>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}

          {/* Conversion funnel */}
          {funnel.length > 0 && (
            <Card title={<Space><RightOutlined style={{ color: TEAL }} /> Engagement Funnel</Space>}
              style={{ borderRadius: 10 }}
              extra={<Text type="secondary" style={{ fontSize: 11 }}>How delegates progressed through the event</Text>}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {funnel.map((stage, i) => {
                  const widthPct = Math.max((stage.count / maxFunnel) * 100, 8);
                  return (
                    <div key={stage.key}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <Space>
                          <span style={{ fontSize: 22 }}>{stage.icon}</span>
                          <Text strong>{stage.label}</Text>
                          {i > 0 && (
                            <Tag color={stage.pct_of_prev >= 70 ? 'green' : stage.pct_of_prev >= 40 ? 'orange' : 'red'}>
                              {stage.pct_of_prev}% conversion from above
                            </Tag>
                          )}
                        </Space>
                        <Space>
                          <Text strong style={{ color: stage.color, fontSize: 18 }}>{stage.count.toLocaleString()}</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>({stage.pct_of_total}%)</Text>
                        </Space>
                      </div>
                      <div style={{
                        height: 28, background: '#f0f0f0', borderRadius: 6, overflow: 'hidden',
                      }}>
                        <div style={{
                          width: `${widthPct}%`, height: '100%',
                          background: `linear-gradient(90deg, ${stage.color} 0%, ${stage.color}cc 100%)`,
                          transition: 'width 0.6s ease',
                          display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                          paddingRight: 12, color: '#fff', fontWeight: 700, fontSize: 12,
                        }}>
                          {stage.count.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
